import { memo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, ClickAwayListener, IconButton, MenuItem, MenuList, Popper, Tooltip } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import OcrTextScanIcon from 'components/icons/OcrTextScanIcon';
import { STREAM_FOOTER_ICON_STYLE } from 'components/transaction/components/panels/transaction/cameras/MediaSection/constants';
import { OCR_IDENTIFIER, OCR_IDENTIFIER_LABEL } from 'utils/ocrVerification';
import styles from './OcrSnapshotButton.module.css';

// Short open delay so sweeping the mouse along the footer does not flash the menu; the close delay lets the pointer
// travel from the icon into the menu.
const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 200;

// Above the camera footer, like the PTZ menu.
const POPPER_STYLE = { zIndex: 10002 };
const POPPER_MODIFIERS = [{ name: 'offset', options: { offset: [0, 4] } }];

const ICON_BUTTON_SX = { padding: '4px', position: 'relative', '&.Mui-disabled': { opacity: 0.5 } };
const SPINNER_SX = { color: 'var(--color-primary-main)', position: 'absolute', top: '50%', left: '50%', marginTop: '-0.5rem', marginLeft: '-0.5rem' };
const MENU_ITEM_SX = {
    minHeight: 'auto',
    padding: '4px 16px',
    justifyContent: 'space-between',
    '&:hover, &.Mui-focusVisible': { backgroundColor: 'var(--primary-states-hover)' },
};
const ITEM_SPINNER_SX = { marginLeft: '24px', color: 'var(--color-primary-main)' };

/**
 * The camera's "Get text from photo" action (Figma 16658:17604): a click reads the default identifier (the first
 * option); hovering shows the menu to read another one. An identifier whose read is in flight cannot be started again.
 */
function OcrSnapshotButton({ options, inFlightIdentifiers, disabled, tooltip, onExtract }) {
    const [open, setOpen] = useState(false);
    // Only a keyboard-opened menu takes focus: opening on hover must not pull focus out of the field being typed in.
    const [focusMenu, setFocusMenu] = useState(false);
    const anchorRef = useRef(null);
    const timerRef = useRef(null);
    const [defaultIdentifier] = options;
    const isAnyInFlight = options.some((identifier) => inFlightIdentifiers.includes(identifier));
    const isDefaultInFlight = inFlightIdentifiers.includes(defaultIdentifier);

    const clearTimer = () => clearTimeout(timerRef.current);
    useEffect(() => clearTimer, []);
    useEffect(() => {
        if (disabled) setOpen(false);
    }, [disabled]);

    const scheduleOpen = useCallback(() => {
        clearTimer();
        if (!disabled) {
            timerRef.current = setTimeout(() => {
                setFocusMenu(false);
                setOpen(true);
            }, OPEN_DELAY_MS);
        }
    }, [disabled]);

    const scheduleClose = useCallback(() => {
        clearTimer();
        timerRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
    }, []);

    const extract = useCallback(
        (identifier) => {
            clearTimer();
            setOpen(false);
            if (disabled || inFlightIdentifiers.includes(identifier)) return;
            onExtract(identifier);
        },
        [disabled, inFlightIdentifiers, onExtract]
    );

    const handleIconClick = (e) => {
        e.stopPropagation();
        extract(defaultIdentifier);
    };

    const handleIconKeyDown = (e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            clearTimer();
            if (!disabled) {
                setFocusMenu(true);
                setOpen(true);
            }
        }
    };

    const handleMenuKeyDown = (e) => {
        if (e.key === 'Escape' || e.key === 'Tab') {
            e.preventDefault();
            setOpen(false);
            anchorRef.current?.focus();
        }
    };

    return (
        <span onMouseEnter={scheduleOpen} onMouseLeave={scheduleClose}>
            <Tooltip placement="top" title={open ? '' : tooltip} disableInteractive>
                <span>
                    <IconButton
                        ref={anchorRef}
                        size="small"
                        aria-label="Get text from photo"
                        aria-haspopup="menu"
                        aria-expanded={open}
                        onClick={handleIconClick}
                        onKeyDown={handleIconKeyDown}
                        disabled={disabled || isDefaultInFlight}
                        sx={ICON_BUTTON_SX}
                    >
                        <span className={styles.icon}>
                            <CameraAltIcon
                                fontSize="small"
                                style={{
                                    ...STREAM_FOOTER_ICON_STYLE,
                                    color: 'var(--color-white)',
                                    opacity: isAnyInFlight ? 0.3 : 1,
                                    transition: 'opacity 0.15s ease',
                                }}
                            />
                            <span className={styles.badge}>
                                <OcrTextScanIcon />
                            </span>
                        </span>
                        {isAnyInFlight && <CircularProgress size={16} sx={SPINNER_SX} />}
                    </IconButton>
                </span>
            </Tooltip>
            <Popper open={open} anchorEl={anchorRef.current} placement="top-end" style={POPPER_STYLE} modifiers={POPPER_MODIFIERS}>
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <div className={styles.menu} onMouseEnter={clearTimer} onMouseLeave={scheduleClose} onClick={(e) => e.stopPropagation()}>
                        <div className={styles['menu-title']}>Get text from photo</div>
                        <MenuList autoFocusItem={open && focusMenu} dense disablePadding onKeyDown={handleMenuKeyDown}>
                            {options.map((identifier) => {
                                const inFlight = inFlightIdentifiers.includes(identifier);
                                return (
                                    <MenuItem key={identifier} disabled={inFlight} onClick={() => extract(identifier)} sx={MENU_ITEM_SX}>
                                        <span className={styles['menu-item-label']}>{OCR_IDENTIFIER_LABEL[identifier]}</span>
                                        {inFlight ? (
                                            <CircularProgress size={12} sx={ITEM_SPINNER_SX} aria-label="Reading" />
                                        ) : (
                                            identifier === defaultIdentifier && <span className={styles['menu-item-hint']}>Default</span>
                                        )}
                                    </MenuItem>
                                );
                            })}
                        </MenuList>
                    </div>
                </ClickAwayListener>
            </Popper>
        </span>
    );
}

OcrSnapshotButton.propTypes = {
    options: PropTypes.arrayOf(PropTypes.oneOf(Object.values(OCR_IDENTIFIER))).isRequired,
    inFlightIdentifiers: PropTypes.arrayOf(PropTypes.string).isRequired,
    disabled: PropTypes.bool,
    tooltip: PropTypes.node,
    onExtract: PropTypes.func.isRequired,
};

export default memo(OcrSnapshotButton);
